import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { Plus, Search, Briefcase, Edit3 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export function PracticesListPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [phase, setPhase] = useState("");
  const [status, setStatus] = useState("");
  const [lookups, setLookups] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (phase) params.phase_id = phase;
    if (status) params.status_id = status;
    try {
      const r = await api.get("/practices", { params });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch { setItems([]); setTotal(0); }
    setLoading(false);
  };

  useEffect(() => { api.get("/lookups/all").then(r => setLookups(r.data)).catch(() => setLookups({phases:[],statuses:[],outcomes:[],service_type_map:{}})); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [phase, status]);

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1 className="ma-h1" data-testid="practices-title">Pratiche</h1>
        <Link to="/practices/new" className="ma-btn-primary-sm" data-testid="btn-new-practice">
          <Plus size={16}/> Nuova pratica
        </Link>
      </div>
      <div className="ma-filters">
        <div className="ma-search">
          <Search size={16}/>
          <input placeholder="Cerca codice o titolo…" value={q} onChange={e=>setQ(e.target.value)}
                 onKeyDown={e=>e.key==="Enter" && load()} data-testid="practices-search"/>
        </div>
        <select className="ma-input-sm" value={phase} onChange={e=>setPhase(e.target.value)} data-testid="filter-phase">
          <option value="">Tutte le fasi</option>
          {lookups?.phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="ma-input-sm" value={status} onChange={e=>setStatus(e.target.value)} data-testid="filter-status">
          <option value="">Tutti gli stati</option>
          {lookups?.statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="ma-total">Totale: <strong>{total}</strong></div>
      </div>

      <div className="ma-table-wrap">
        {loading ? <div className="ma-loading">Caricamento…</div> : (
          <table className="ma-table">
            <thead><tr>
              <th>Codice</th><th>Titolo</th><th>Cliente</th><th>Servizio</th>
              <th>Fase</th><th>Stato</th><th>Scadenza</th><th>Priorità</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="8" className="ma-empty">Nessuna pratica trovata.</td></tr>}
              {items.map(p => (
                <tr key={p.id} data-testid={`practice-row-${p.id}`}>
                  <td className="ma-mono"><Link to={`/practices/${p.id}`} className="ma-link">{p.code}</Link></td>
                  <td>{p.title}</td>
                  <td>{p.subject?.display_name || "—"}</td>
                  <td>{lookups?.service_type_map?.[p.service_type] || p.service_type}</td>
                  <td>{p.phase?.name || "—"}</td>
                  <td>{p.status && <span className="ma-pill" style={{background: p.status.color || "#e2e2e2", color:"#fff"}}>{p.status.name}</span>}</td>
                  <td>{p.deadline ? new Date(p.deadline).toLocaleDateString("it-IT") : "—"}</td>
                  <td>{p.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function PracticeCreatePage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [lookups, setLookups] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [f, setF] = useState({
    title:"", subject_id: params.get("subject") || "", service_type: "finanza_agevolata",
    tool_note:"", opening_date:"", deadline:"", priority:"media",
    phase_id:"", status_id:"", project_amount:"", requested_grant:"", description:"",
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/lookups/all").then(r => setLookups(r.data));
    api.get("/subjects?limit=200").then(r => setSubjects(r.data.items));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const payload = { ...f };
      ["project_amount","requested_grant"].forEach(k => payload[k] = f[k] ? parseFloat(f[k]) : null);
      ["opening_date","deadline","phase_id","status_id","tool_note","description"].forEach(k => { if (!payload[k]) payload[k] = null; });
      const r = await api.post("/practices", payload);
      nav(`/practices/${r.data.id}`);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  return (
    <div className="ma-page">
      <h1 className="ma-h1" data-testid="new-practice-title">Nuova pratica</h1>
      <form onSubmit={submit} className="ma-form-panel">
        <div className="ma-form-grid">
          <div className="ma-col-2"><label className="ma-label">Titolo *</label>
            <input className="ma-input" required value={f.title} onChange={e=>setF({...f, title:e.target.value})} data-testid="practice-title"/></div>
          <div className="ma-col-2"><label className="ma-label">Cliente *</label>
            <select className="ma-input" required value={f.subject_id} onChange={e=>setF({...f, subject_id:e.target.value})} data-testid="practice-subject">
              <option value="">— Seleziona —</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.display_name}</option>)}
            </select>
          </div>
          <div><label className="ma-label">Tipo servizio *</label>
            <select className="ma-input" value={f.service_type} onChange={e=>setF({...f, service_type:e.target.value})} data-testid="practice-service">
              {lookups?.service_types.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </div>
          <div><label className="ma-label">Strumento / misura</label>
            <input className="ma-input" value={f.tool_note} onChange={e=>setF({...f, tool_note:e.target.value})}/></div>
          <div><label className="ma-label">Data apertura</label>
            <input type="date" className="ma-input" value={f.opening_date} onChange={e=>setF({...f, opening_date:e.target.value})}/></div>
          <div><label className="ma-label">Scadenza</label>
            <input type="date" className="ma-input" value={f.deadline} onChange={e=>setF({...f, deadline:e.target.value})}/></div>
          <div><label className="ma-label">Priorità</label>
            <select className="ma-input" value={f.priority} onChange={e=>setF({...f, priority:e.target.value})}>
              <option value="bassa">Bassa</option><option value="media">Media</option>
              <option value="alta">Alta</option><option value="urgente">Urgente</option>
            </select>
          </div>
          <div><label className="ma-label">Fase</label>
            <select className="ma-input" value={f.phase_id} onChange={e=>setF({...f, phase_id:e.target.value})}>
              <option value="">—</option>
              {lookups?.phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className="ma-label">Stato</label>
            <select className="ma-input" value={f.status_id} onChange={e=>setF({...f, status_id:e.target.value})}>
              <option value="">—</option>
              {lookups?.statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div><label className="ma-label">Importo progetto (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.project_amount} onChange={e=>setF({...f, project_amount:e.target.value})}/></div>
          <div><label className="ma-label">Contributo richiesto (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.requested_grant} onChange={e=>setF({...f, requested_grant:e.target.value})}/></div>
          <div className="ma-col-full"><label className="ma-label">Descrizione</label>
            <textarea className="ma-input" rows={3} value={f.description} onChange={e=>setF({...f, description:e.target.value})}/></div>
        </div>
        {err && <div className="ma-error">{err}</div>}
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={()=>nav(-1)}>Annulla</button>
          <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="submit-new-practice">
            {busy ? "Creazione…" : "Crea pratica"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function PracticeDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [editing, setEditing] = useState(false);
  const { has } = useAuth();

  const load = () => api.get(`/practices/${id}`).then(r => setP(r.data));
  useEffect(() => { load(); api.get("/lookups/all").then(r => setLookups(r.data)); }, [id]);

  const changePhase = async (phase_id) => {
    try {
      await api.patch(`/practices/${id}`, { phase_id: phase_id || null });
      load();
    } catch { /* toast */ }
  };
  const changeStatus = async (status_id) => {
    const newStatus = lookups.statuses.find(x => x.id === status_id) || null;
    const currentStatus = p.status;
    // Riapertura: da finale a non finale (o vuoto) con esito valorizzato
    if (currentStatus?.is_closing && (!newStatus || !newStatus.is_closing) && p.outcome) {
      if (!window.confirm("Stai riaprendo una pratica chiusa/rinunciata. L'esito verrà azzerato e la riapertura sarà registrata nello storico. Confermi?")) return;
    }
    try {
      // Se si passa a stato non-finale, azzera l'esito nella stessa chiamata
      const payload = { status_id: status_id || null };
      if (!newStatus || !newStatus.is_closing) payload.outcome_id = null;
      // Se si passa a stato finale ed esito manca, il backend risponde 400: gestito dal toast
      await api.patch(`/practices/${id}`, payload);
      load();
    } catch { /* toast */ }
  };
  const changeOutcome = async (outcome_id) => {
    try {
      await api.patch(`/practices/${id}`, { outcome_id: outcome_id || null });
      load();
    } catch { /* toast */ }
  };

  const isClosing = !!p?.status?.is_closing;

  if (!p || !lookups) return <div className="ma-loading">Caricamento…</div>;

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to="/practices">← Pratiche</Link></div>
          <h1 className="ma-h1" data-testid="practice-title">
            <span className="ma-mono ma-code-badge">{p.code}</span> {p.title}
          </h1>
          <div className="ma-sub">
            <Link to={`/clients/${p.subject.id}`} className="ma-link">{p.subject.display_name}</Link>
            <span>·</span>
            <span>{lookups.service_type_map[p.service_type] || p.service_type}</span>
            {p.deadline && <><span>·</span><span>Scadenza: {new Date(p.deadline).toLocaleDateString("it-IT")}</span></>}
          </div>
        </div>
        <div className="ma-actions">
          {has("practices.write") && (
            <Link to={`/practices/${p.id}/edit`} className="ma-btn-primary-sm" data-testid="btn-edit-practice">
              <Edit3 size={14}/> Modifica pratica
            </Link>
          )}
        </div>
      </div>

      <div className="ma-panel-grid">
        <div className="ma-panel">
          <div className="ma-panel-head">Stato pratica</div>
          <div className="ma-panel-body">
            <div className="ma-row-kv"><div className="ma-row-k">Fase</div>
              <div className="ma-row-v">
                {has("practices.write") ? (
                  <select className="ma-input-sm" value={p.phase?.id || ""} onChange={e=>changePhase(e.target.value)} data-testid="practice-phase-select">
                    <option value="">—</option>
                    {lookups.phases.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                ) : (p.phase?.name || "—")}
              </div>
            </div>
            <div className="ma-row-kv"><div className="ma-row-k">Stato</div>
              <div className="ma-row-v">
                {has("practices.write") ? (
                  <select className="ma-input-sm" value={p.status?.id || ""} onChange={e=>changeStatus(e.target.value)} data-testid="practice-status-select">
                    <option value="">—</option>
                    {lookups.statuses.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                ) : (p.status?.name || "—")}
              </div>
            </div>
            <div className="ma-row-kv"><div className="ma-row-k">Esito</div>
              <div className="ma-row-v">
                {has("practices.write") ? (
                  <select className="ma-input-sm" value={p.outcome?.id || ""} onChange={e=>changeOutcome(e.target.value)} disabled={!isClosing} data-testid="practice-outcome-select">
                    <option value="">—</option>
                    {lookups.outcomes.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </select>
                ) : (p.outcome?.name || "—")}
                {!isClosing && <div className="ma-hint">Impostabile solo con stato finale.</div>}
              </div>
            </div>
            <div className="ma-row-kv"><div className="ma-row-k">Priorità</div><div className="ma-row-v">{p.priority}</div></div>
          </div>
        </div>

        <div className="ma-panel">
          <div className="ma-panel-head">Dati economici</div>
          <div className="ma-panel-body">
            <div className="ma-row-kv"><div className="ma-row-k">Importo progetto</div><div className="ma-row-v">{p.project_amount ? `€ ${p.project_amount.toLocaleString("it-IT")}` : "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Contributo richiesto</div><div className="ma-row-v">{p.requested_grant ? `€ ${p.requested_grant.toLocaleString("it-IT")}` : "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Finanziamento richiesto</div><div className="ma-row-v">{p.requested_loan ? `€ ${p.requested_loan.toLocaleString("it-IT")}` : "—"}</div></div>
            <div className="ma-row-kv"><div className="ma-row-k">Mezzi propri</div><div className="ma-row-v">{p.own_funds ? `€ ${p.own_funds.toLocaleString("it-IT")}` : "—"}</div></div>
          </div>
        </div>

        {p.description && (
          <div className="ma-panel ma-col-full">
            <div className="ma-panel-head">Descrizione</div>
            <div className="ma-panel-body"><div className="ma-notes">{p.description}</div></div>
          </div>
        )}

        <div className="ma-panel ma-col-full">
          <div className="ma-panel-head">Storico stato / fase / esito</div>
          <div className="ma-panel-body">
            {p.history.length === 0 && <div className="ma-empty">Nessuna variazione di fase/stato/esito registrata.</div>}
            {p.history.map(h => (
              <div key={h.id} className="ma-activity-row" data-testid={`history-row-${h.id}`}>
                <div className="ma-activity-dot"/>
                <div className="ma-activity-text">
                  {h.note ? h.note : "Variazione di fase/stato/esito"}
                </div>
                <div className="ma-activity-time">{new Date(h.changed_at).toLocaleString("it-IT")}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="ma-panel ma-col-full">
          <div className="ma-panel-head">Modifiche registrate (audit)</div>
          <div className="ma-panel-body">
            {(!p.audit_updates || p.audit_updates.length === 0) && <div className="ma-empty">Nessuna modifica registrata.</div>}
            {(p.audit_updates || []).map(a => (
              <div key={a.id} className="ma-audit-entry" data-testid={`audit-entry-${a.id}`}>
                <div className="ma-audit-head">
                  <span className="ma-audit-user">{a.user_name}</span>
                  <span className="ma-audit-when">{new Date(a.at).toLocaleString("it-IT")}</span>
                </div>
                <table className="ma-audit-table">
                  <thead><tr><th>Campo</th><th>Valore precedente</th><th>Nuovo valore</th></tr></thead>
                  <tbody>
                    {a.changes.map((c, idx) => (
                      <tr key={idx}>
                        <td className="ma-audit-field">{c.label}</td>
                        <td className="ma-audit-old">{c.old || <span className="ma-muted">—</span>}</td>
                        <td className="ma-audit-new">{c.new || <span className="ma-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
